import {
  FLEET_LANE_PATTERN,
  FLEET_PROJECT_PATTERN,
  type FleetCommandBody,
  type FleetEngineName,
  type FleetEngineSource,
  type FleetLaneEngine,
} from '@immo/api-contract';

export const ANY_ENGINE = 'any';
export type EngineChoice = FleetEngineName | typeof ANY_ENGINE;

/** What the `engine` dialog holds while the operator fills it — strings as typed. */
export type EngineForm = {
  readonly engine: EngineChoice;
  readonly lane: string;
  readonly project: string;
};

export const EMPTY_ENGINE_FORM: EngineForm = { engine: ANY_ENGINE, lane: '', project: '' };

const PIN_SOURCES: ReadonlyArray<FleetEngineSource> = ['host-project-lane', 'host-lane', 'project'];

/**
 * The form a lane's card opens: that lane, with its own pin preselected and "any" otherwise. A profile that is not
 * a valid lane name (an older host's `dev:engineer`) opens with an empty lane instead of one the host would refuse.
 */
export function engineFormFor(lane: string | null, current: FleetLaneEngine | null | undefined): EngineForm {
  return {
    ...EMPTY_ENGINE_FORM,
    ...(current != null && PIN_SOURCES.includes(current.source) ? { engine: current.engine } : {}),
    lane: lane !== null && FLEET_LANE_PATTERN.test(lane) ? lane : '',
  };
}

/** Which fields the contract would refuse — the dialog marks them and keeps its CTA disabled, so no 400 round-trips. */
export function engineFormErrors(form: EngineForm): { readonly lane: boolean; readonly project: boolean } {
  const lane = form.lane.trim();
  const project = form.project.trim();

  return {
    lane: lane !== '' && !FLEET_LANE_PATTERN.test(lane),
    project: project !== '' && !FLEET_PROJECT_PATTERN.test(project),
  };
}

export function isEngineFormValid(form: EngineForm): boolean {
  return form.lane.trim() !== '' && !Object.values(engineFormErrors(form)).some(Boolean);
}

/** The body for a confirmed form: always a lane, a project only when named, "any" as `null`. */
export function engineBodyFor(form: EngineForm): Extract<FleetCommandBody, { verb: 'engine' }> {
  const project = form.project.trim();

  return {
    verb: 'engine',
    engine: form.engine === ANY_ENGINE ? null : form.engine,
    lane: form.lane.trim(),
    ...(project !== '' ? { project } : {}),
  };
}
